// Shared AppsFlyer tracking layer for the web shop.
//
// AppsFlyer's dashboard integrations forward install/event data on to Meta,
// TikTok, X and Snapchat, so this is the only ad-tracking code the web app
// needs — no per-network pixels.
//
// Two logging surfaces:
//  - Inside the native Expo WebView shell: events are relayed to the native
//    AppsFlyer SDK over the existing postMessage bridge, so an in-app hit is
//    attributed as an app event rather than a web hit.
//  - Standalone browser visits: events are logged directly via the AppsFlyer
//    Web SDK.
// Both stay inert (console.debug fallback) until real credentials replace
// the .env placeholders.

const WEB_DEV_KEY = process.env.REACT_APP_APPSFLYER_WEB_DEV_KEY;
const WEB_APP_ID = process.env.REACT_APP_APPSFLYER_WEB_APP_ID;

const isWebSdkConfigured = () =>
  Boolean(WEB_DEV_KEY) && Boolean(WEB_APP_ID);

export const isInReactNativeWebView = (): boolean =>
  Boolean(window.ReactNativeWebView);

let webSdkLoaded = false;

/**
 * Loads the AppsFlyer Web SDK for standalone browser visits. Skipped inside
 * the native WebView shell, where the native SDK (via `trackEvent`'s
 * postMessage relay) is the source of truth instead.
 *
 * NOTE: replace the loader snippet below with the exact one from the
 * AppsFlyer dashboard once real Web App/Dev credentials are issued — the
 * shape here follows AppsFlyer's documented Web SDK loader but should be
 * verified against the dashboard-generated snippet before going live.
 */
export function initAppsFlyerWeb(): void {
  if (isInReactNativeWebView() || webSdkLoaded || !isWebSdkConfigured()) return;
  webSdkLoaded = true;

  (function (t: Window, d: Document, s: string, src: string) {
    (t as any).AF =
      (t as any).AF ||
      function (...args: unknown[]) {
        ((t as any).AF.q = (t as any).AF.q || []).push(args);
      };
    const script = d.createElement(s);
    script.setAttribute("async", "true");
    (script as HTMLScriptElement).src = src;
    d.head.appendChild(script);
  })(window, document, "script", "https://websdk.appsflyer.com/v1.0.0/websdk.min.js");

  window.AF?.("pba", "init", { appId: WEB_APP_ID, pbaDevKey: WEB_DEV_KEY });
}

/**
 * Logs a standard AppsFlyer event (`af_complete_registration`, `af_login`,
 * `af_initiated_checkout`, `af_content_view`, ...). Routes to the native SDK
 * when running inside the app, otherwise the Web SDK.
 */
export function trackEvent(
  eventName: string,
  eventValues?: Record<string, unknown>,
): void {
  if (isInReactNativeWebView()) {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: "track-event", eventName, eventValues }),
    );
    return;
  }

  if (!isWebSdkConfigured() || typeof window.AF !== "function") {
    console.debug(`[appsflyer] ${eventName}`, eventValues ?? {});
    return;
  }

  window.AF("pba", "event", {
    eventType: "EVENT",
    eventName,
    eventValue: eventValues ?? {},
  });
}

/**
 * Resolves the AppsFlyer Web SDK's user id for the current browser, or null
 * when unavailable (in-app, SDK not configured/loaded yet).
 */
export function getAppsFlyerWebUID(): Promise<string | null> {
  return new Promise((resolve) => {
    if (isInReactNativeWebView() || typeof window.AF !== "function") {
      resolve(null);
      return;
    }
    try {
      window.AF("pba", "getWebUserId", (id: string) => resolve(id ?? null));
    } catch {
      resolve(null);
    }
  });
}
